/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react'
import { connect } from 'react-redux'
import R from 'ramda'
import {
  getApiTargetInfo,
  rowHeaderRender,
  ExportComponent,
  exportDataComponent,
} from 'core'
import { strCol, intCol, dateCol } from '@integec/grid-tools/lib/cols'
import { modifierCol, modifiedDateCol } from 'core/HeaderColTypeProps'
import { ErrorContext, SpinContext } from 'context'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
  Label,
} from 'core/screen-util'
import {
  Grid,
  defaultVirtualizedCellRender,
  virtualizedGridRenderer,
  freeEditEngine,
  toSelectionColProps,
} from '@integec/grid-tools'
import { savePLW, getPLW, deletePLW, updatePLW } from 'api/pgads'
import {
  Select,
  Button,
  message,
  Empty,
  Input,
  Popconfirm,
  DatePicker,
} from 'antd'
import moment from 'moment'
import { DATE_FORMAT_API, DATE_FORMAT_UI } from 'enum'

export const PLW = ({ apiTargetInfo }) => {
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const GridRef = useRef()
  const [popModal, setPopModal] = useState(false)
  const [plwData, setplwData] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  const [selectedRows, setRowsSelected] = useState([])
  const [modalInfo, setModalInfo] = useState({
    season: '',
    year: moment().year(),
    startingHB: '',
    endingHB: '',
  })
  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )

  const loadData = useCallback(() => {
    setSpinning(true)
    getPLW(apiTargetInfo).fork(setError, data => {
      setSpinning(false)
      setplwData(data)
    })
  }, [apiTargetInfo, setError, setSpinning])

  useEffect(() => {
    loadData()
  }, [loadData])

  const exceptionDateValidation = data => {
    const { season, year, exceptionDate } = data
    const yyyy = parseInt(year)
    const startDate =
      season === 'W' ? moment(`${yyyy - 1}-11-01`) : moment(`${yyyy}-05-01`)
    const endDate =
      season === 'W' ? moment(`${yyyy}-04-30`) : moment(`${yyyy}-10-31`)
    return (
      moment(exceptionDate).isBefore(startDate, 'day') ||
      moment(exceptionDate).isAfter(endDate, 'day')
    )
  }

  const validateData = data => {
    const { season, year, startingHB, endingHB, exceptionDate } = data
    if (!season) {
      message.warn('Please select season.')
      return false
    }
    if (!year) {
      message.warn('Please select year.')
      return false
    }
    if (exceptionDate && exceptionDateValidation(data)) {
      message.error(
        'The exception date must fall within the selected year-season.'
      )
      return false
    }
    if (!startingHB) {
      message.warn('Please select starting HB.')
      return false
    }
    if (!endingHB) {
      message.warn('Please select ending HB.')
      return false
    }
    if (parseInt(startingHB) > parseInt(endingHB)) {
      message.error('Ending HB shouldn’t be less than Starting HB.')
      return false
    }
    const pkData = plwData
      .filter(record => !record.updated)
      .filter(record => {
        return (
          record.season === data.season &&
          record.year === data.year &&
          (record.exceptionDate === data.exceptionDate ||
            (!data.exceptionDate && !record.exceptionDate))
        )
      })
    if (pkData.length > 0) {
      message.error(
        'This year-season-exception date has been defined previously.'
      )
      return false
    }
    return true
  }

  const validateDataEachOther = updatedRecords => {
    const hasDuplicate = updatedRecords.some((recordA, index, array) =>
      array.some((recordB, innerIndex) => {
        if (index === innerIndex) return false // Skip comparing the same record
        return (
          recordA.season === recordB.season &&
          recordA.year === recordB.year &&
          recordA.exceptionDate === recordB.exceptionDate
        )
      })
    )
    if (hasDuplicate) {
      message.error(
        'This year-season-exception date has been defined previously.'
      )
      return false
    }
    return true
  }
  const onSaveData = (data, hide) => {
    if (!validateData(data)) return
    setSpinning(true)
    savePLW(apiTargetInfo)([data]).fork(setError, () => {
      setSpinning(false)
      if (!hide) {
        setPopModal(false)
      }
      setModalInfo({})
      message.success('Peak Load Window data saved successfully')
      loadData()
    })
  }

  const onUpdate = () => {
    const saveData = freeEditEngine.applyEdits({
      data: plwData?.filter(d => d.updated),
      editInfo,
    })
    if (saveData.length <= 0) {
      message.error('Please, update data')
      return
    }
    const allValid = saveData.every(d => validateData(d))
    if (!allValid) {
      return
    }
    const updatedRecordValidation = validateDataEachOther(saveData)
    if (!updatedRecordValidation) {
      return
    }
    setSpinning(true)
    updatePLW(apiTargetInfo)(saveData).fork(setError, () => {
      setSpinning(false)
      setEditInfo(freeEditEngine.generateInitialEditInfo())
      message.success('Peak Load Window data updated successfully')
      loadData()
    })
  }

  const onDelete = () => {
    setSpinning(true)
    deletePLW(apiTargetInfo)(selectedRows).fork(setError, () => {
      setSpinning(false)
      message.success('Peak Load Window data deleted successfully')
      loadData()
    })
  }

  const headerWithFormatter = header.map(h =>
    h.ident === 'season'
      ? {
          ...h,
          dataFormatter: ({ value }) => (value === 'W' ? 'Winter' : 'Summer'),
        }
      : h.ident === 'year'
      ? {
          ...h,
          dataFormatter: ({ value, rowData }) =>
            rowData.season === 'W' ? `${value - 1}-${value}` : value,
        }
      : h.ident === 'length'
      ? {
          ...h,
          dataFormatter: ({ rowData }) =>
            rowData.endingHB - rowData.startingHB + 1,
        }
      : h
  )

  const isEditableComp = ({ header }) => {
    const { ident } = header
    if (
      ident === 'startingHB' ||
      ident === 'endingHB' ||
      ident === 'exceptionDate'
    )
      return true
    return false
  }

  const editRow = editInfo => (data, setEditInfo, setData) => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      ...d,
      modificationInfo: undefined,
    }))
    setEditInfo({ ...editInfo, updated: editData })
    setData(modData)
  }

  const dateEditor = props => {
    const { data, header, rowIndex, style, getInputProps, cancelEdit } = props
    const { onChange, value } = getInputProps({ refKey: 'innerRef' })
    const { year: currentYear, season: currentSeason } = data[rowIndex]
    return (
      <DatePicker
        allowClear
        value={value ? moment(value) : null}
        disabledDate={current => {
          if (!current) return true

          const y = parseInt(currentYear)

          if (currentSeason === 'S') {
            const start = moment(`${y}-05-01`)
            const end = moment(`${y}-10-31`)
            return current.isBefore(start, 'day') || current.isAfter(end, 'day')
          }

          if (currentSeason === 'W') {
            const start = moment(`${y - 1}-11-01`)
            const end = moment(`${y}-04-30`)
            return current.isBefore(start, 'day') || current.isAfter(end, 'day')
          }

          // Default to disabling everything if season is invalid
          return true
        }}
        onChange={date => {
          const newEditInfo = freeEditEngine.updateRow({
            editInfo,
            currentRow: data[rowIndex],
            editedRow: {
              ...data[rowIndex],
              [header.ident]: date ? date.format(header.dataFormat) : null,
            },
          })
          editRow(newEditInfo)(plwData, setEditInfo, setplwData)
          data[rowIndex][header.ident] = date
            ? date.format(header.dataFormat)
            : null
          onChange({
            target: { value: date ? date.format(header.dataFormat) : date },
          })
        }}
        style={{
          ...style,
          padding: `0px`,
          margin: `0px`,
          paddingLeft: `10px`,
          borderTop: `0px solid #fff`,
        }}
        format={header.displayFormat}
        size="small"
        onOpenChange={status => {
          if (!status) cancelEdit()
        }}
        suffixIcon={null}
      />
    )
  }

  const cellRender = props => {
    const { gridToolProps, reactVirtualizedProps } = props
    const { style } = reactVirtualizedProps
    const type = gridToolProps.headers[reactVirtualizedProps.columnIndex].type
    return defaultVirtualizedCellRender({
      ...props,
      style,
      editRender:
        type === 'date' || type === 'date-time' ? dateEditor : undefined,
    })
  }

  const getGridWidth = defaultWidth => {
    const headerWidth = header.reduce((acc, h) => acc + (h.width || 130), 0)
    return Math.max(headerWidth, defaultWidth)
  }

  return (
    <>
      <div
        style={{
          backgroundColor: '#f0f2f5',
          marginLeft: '20px',
          marginRight: '20px',
        }}
      >
        <CriteriaRow
          style={{
            display: 'flex',
            width: '100%',
          }}
        >
          <Title
            style={{
              marginRight: '15px',
              marginBottom: '5px',
              minWidth: '170px',
            }}
          >
            {title}
          </Title>
          <Button
            onClick={() => loadData()}
            type="primary"
            icon="database"
            style={{ marginLeft: '10px' }}
          >
            Retrieve
          </Button>
          <Button
            onClick={() => {
              setPopModal(true)
            }}
            icon="plus-circle"
            style={{ backgroundColor: '#e6f7ff', marginLeft: '10px' }}
          >
            Insert
          </Button>
          <Button
            onClick={onUpdate}
            icon="edit"
            type="primary"
            disabled={editInfo.updated.length <= 0}
            style={{ marginLeft: '10px' }}
          >
            Update
          </Button>
          <Popconfirm
            title={
              <span>
                The selected PLW record shall be permanently deleted.
                <br />
                Select Continue to proceed or Cancel.
              </span>
            }
            okText="Continue"
            cancelText="Cancel"
            onConfirm={onDelete}
            placement="topRight"
            okType="danger"
          >
            <Button
              type="danger"
              disabled={selectedRows.length <= 0}
              icon="delete"
              style={{ marginLeft: '10px' }}
            >
              Delete
            </Button>
          </Popconfirm>
          <Filler />
          <Input.Search
            placeholder="Filter"
            value={fuzzyFilter}
            style={{
              marginLeft: '10px',
              marginRight: '10px',
              flex: '0 0 200px',
            }}
            onChange={e => setFuzzyFilter(e.target.value)}
          />

          <ExportComponent
            getData={_ => exportDataComponent(GridRef, plwData, fuzzyFilter)}
            data={plwData}
            headers={headerWithFormatter}
            fileName={title}
            btnStyle={{ marginRight: '10px' }}
            disabled={!!(plwData && plwData.length <= 0)}
          />
        </CriteriaRow>
        {popModal && (
          <CriteriaRow css={{ marginTop: 10 }}>
            <EditModal
              modalInfo={modalInfo}
              setModalInfo={setModalInfo}
              handleSave={onSaveData}
              hide={() => setPopModal(false)}
              processing={spinning}
            />
          </CriteriaRow>
        )}
        <FullHeightSizer heightOffset={190}>
          {({ height, width }) =>
            plwData && plwData.length > 0 ? (
              <Content style={{ height: height - 20 - (popModal ? 120 : 0) }}>
                <Grid
                  ref={GridRef}
                  sortEnabled
                  data={plwData}
                  headers={headerWithFormatter}
                  altBy={u => u.year}
                  altBgColor="#ebebeb"
                  render={virtualizedGridRenderer({
                    rowHeaderRender,
                    height: height - 40 - (popModal ? 120 : 0),
                    width: getGridWidth(width - 800),
                    cellRender: cellRender,
                    selectionMode: 'single',
                    headerRowHeight: 30,
                  })}
                  onEditInfoChange={info =>
                    editRow(info)(plwData, setEditInfo, setplwData)
                  }
                  editInfo={editInfo}
                  fuzzyFilter={fuzzyFilter}
                  isEditable={isEditableComp}
                  editMode="cell"
                  onSelectionChange={rows => setRowsSelected(rows.selectedRows)}
                  selectionType="row"
                  selectionMode="single"
                  initialSortOptions={headerWithFormatter}
                  // sortOptions={headerWithFormatter}
                />
              </Content>
            ) : (
              <Content style={{ height: '200px' }}>
                <Empty
                  imageStyle={{ paddingTop: '30px' }}
                  description={
                    plwData && plwData.length <= 0
                      ? 'No Data from Retrieve'
                      : 'Please click on retrieve button to retrieve data'
                  }
                />
              </Content>
            )
          }
        </FullHeightSizer>
      </div>
    </>
  )
}
const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
})

export default connect(mapStateToProps)(PLW)
const EditModal = ({ handleSave, hide, modalInfo, setModalInfo }) => {
  const onOk = show => {
    handleSave(modalInfo, show)
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '200px 200px 200px 200px 200px 200px',
        gap: 10,
      }}
    >
      <div>
        <Label style={{ display: 'inline', float: 'right' }}>Season :</Label>
        <span style={{ color: 'red', marginRight: '2px', float: 'right' }}>
          *
        </span>
      </div>
      <Select
        showSearch
        placeholder="Season"
        style={{ marginLeft: '10px', width: '200px' }}
        value={modalInfo.season}
        onChange={value =>
          setModalInfo({
            ...modalInfo,
            season: value,
          })
        }
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        <Select.Option value="W" key="W">
          Winter
        </Select.Option>
        <Select.Option value="S" key="S">
          Summer
        </Select.Option>
      </Select>
      <div>
        <Label style={{ display: 'inline', float: 'right' }}>Year :</Label>
        <span style={{ color: 'red', marginRight: '2px', float: 'right' }}>
          *
        </span>
      </div>
      <Select
        showSearch
        placeholder="year"
        style={{ marginLeft: '10px', width: '200px' }}
        value={modalInfo.year}
        onChange={value =>
          setModalInfo({
            ...modalInfo,
            year: value,
            exceptionDate: '',
          })
        }
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {modalInfo.season === 'W' ? winteryYearOption : summerYearOption}
      </Select>
      <div>
        <Label style={{ display: 'inline', float: 'right' }}>
          Exception Date :
        </Label>
      </div>
      <DatePicker
        allowClear
        format="MM/DD/YYYY"
        disabledDate={current => {
          if (!current) return true

          const y = parseInt(modalInfo.year)

          if (modalInfo.season === 'S') {
            const start = moment(`${y}-05-01`)
            const end = moment(`${y}-10-31`)
            return current.isBefore(start, 'day') || current.isAfter(end, 'day')
          }

          if (modalInfo.season === 'W') {
            const start = moment(`${y - 1}-11-01`)
            const end = moment(`${y}-04-30`)
            return current.isBefore(start, 'day') || current.isAfter(end, 'day')
          }

          // Default to disabling everything if season is invalid
          return true
        }}
        value={
          modalInfo.exceptionDate
            ? moment(modalInfo.exceptionDate, DATE_FORMAT_API)
            : null
        }
        onChange={date =>
          setModalInfo({
            ...modalInfo,
            exceptionDate: date ? moment(date).format(DATE_FORMAT_API) : date,
          })
        }
      />
      <div>
        <Label style={{ display: 'inline', float: 'right' }}>
          Starting HB :
        </Label>
        <span style={{ color: 'red', marginRight: '2px', float: 'right' }}>
          *
        </span>
      </div>

      <Select
        showSearch
        placeholder="Starting HB"
        style={{ marginLeft: '10px', width: '200px' }}
        value={modalInfo.startingHB}
        onChange={value =>
          setModalInfo({
            ...modalInfo,
            startingHB: value,
          })
        }
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {hoursOption}
      </Select>

      <div>
        <Label style={{ display: 'inline', float: 'right' }}>Ending HB :</Label>
        <span style={{ color: 'red', marginRight: '2px', float: 'right' }}>
          *
        </span>
      </div>

      <Select
        showSearch
        placeholder="Ending HB"
        style={{ marginLeft: '10px', width: '200px' }}
        value={modalInfo.endingHB}
        onChange={value =>
          setModalInfo({
            ...modalInfo,
            endingHB: value,
          })
        }
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {hoursOption}
      </Select>

      <div
        style={{
          display: 'grid',
          gridColumnEnd: 'span 2',
          gridTemplateColumns: 'auto auto auto',
          gap: 10,
          marginLeft: '30px',
        }}
      >
        <Button type="primary" onClick={() => onOk(true)}>
          Add
        </Button>

        <Button type="primary" onClick={() => onOk(false)}>
          Add & Hide
        </Button>

        <Button type="danger" onClick={_ => hide()}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export const title = 'Peak Load Window'
const curYear = new Date().getFullYear()
export const summerYearOption = R.range(curYear - 15, curYear + 10)
  .sort((a, b) => b - a)
  .map(i => (
    <Select.Option value={i} key={i}>
      {`${i}`}
    </Select.Option>
  ))

export const winteryYearOption = R.range(curYear - 15, curYear + 10)
  .sort((a, b) => b - a)
  .map(i => (
    <Select.Option value={i} key={i}>
      {`${i - 1}-${i}`}
    </Select.Option>
  ))

export const hoursOption = R.range(1, 25).map(i => (
  <Select.Option value={i} key={i}>
    {String(i).padLeft(2, '0')}
  </Select.Option>
))

export const hoursMap = R.compose(
  R.fromPairs,
  R.map(i => [i, String(i).padLeft(2, '0')])
)(R.range(1, 26))

export const header = [
  intCol({
    ident: 'year',
    display: 'Year',
    isKey: true,
    isFilterable: true,
    width: 120,
  }),
  strCol({
    ident: 'season',
    display: 'Season',
    isFilterable: true,
    isKey: true,
    width: 120,
  }),
  dateCol({
    ident: 'exceptionDate',
    display: 'Exception Date',
    isFilterable: true,
    dataFormat: DATE_FORMAT_API,
    displayFormat: DATE_FORMAT_UI,
    width: 120,
  }),
  intCol({
    ident: 'startingHB',
    display: 'Starting HB',
    isFilterable: true,
    width: 120,
    ...toSelectionColProps(hoursMap),
  }),
  intCol({
    ident: 'endingHB',
    display: 'Ending HB',
    isFilterable: true,
    width: 120,
    ...toSelectionColProps(hoursMap),
  }),
  intCol({
    ident: 'length',
    display: 'Length',
    isFilterable: true,
    width: 120,
  }),
  modifierCol,
  modifiedDateCol,
].map(h => ({ ...h, ellipsis: true, sortOrder: 'asc' }))
